import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  isUploadComplete,
  type ChatFile,
  type ChatMessageItem,
  type ChatReplyRef,
  type ChatUser,
} from "@altek/ui/models/chat/chat-types";
import { openFileExternally } from "@altek/ui/components/form/FileUploadField";
import { useAuth } from "@/app/providers/AuthProvider";
import { isRestrictedSupportSession } from "@/lib/auth/support-session";
import { toast } from "@altek/ui/lib/utils/toast";
import { chatApi } from "../api/chat.api";
import { chatConversationQuery, chatMessagesQuery } from "../api/chat.queries";
import { formatCount } from "@altek/ui/lib/format/number";
import {
  CHAT_PERMISSIONS,
  MESSAGE_MAX_LENGTH,
  type ChatConversationDetail,
  type ChatMessageRow,
} from "../api/chat.types";
import { chatErrorMessage } from "../lib/chat-errors";
import { createDraftStore, type DraftStore } from "../lib/chat-draft-store";
import { localId } from "../lib/chat-ids";
import {
  composerBlockReason,
  firstUnreadMessageId,
  memberIndex,
  messageItem,
  newestFromOthers,
  orderOldestFirst,
  peerOf,
  replyRefOf,
} from "../lib/chat-map";
import {
  invalidateBell,
  invalidateChat,
  invalidateChatCounters,
  typingUsersFrom,
} from "../lib/chat-invalidate";
import { useTypingUserIds } from "../realtime/typing-store";
import { useChatAttachments } from "./useChatAttachments";
import { usePageAttention } from "./usePageAttention";

/**
 * One conversation, fully wired — used by the full-screen page AND by the dock.
 *
 * WHY ONE HOOK FOR BOTH SURFACES. The dock and the page are the same conversation with a
 * different amount of room. Written twice, they drift in exactly the ways that matter: one marks
 * read and the other does not, one reverses the cursor pages and the other shows the thread
 * upside down, one sends with an idempotency key and the other duplicates a message on a flaky
 * connection. The geometry is the components' business; the behaviour is here.
 *
 * THE OUTBOX IS NOT AN OPTIMISTIC WRITE. Nothing is ever spliced into the query cache. What the
 * panel renders is the server's thread, plus a short local list of messages THIS user typed and
 * this client has not yet had confirmed. That distinction is the whole mutation contract: a
 * confirmed message comes from the server or it does not exist, while a message the user typed
 * must stay on screen even when the send fails — `chat-types.ts` calls a failed send that erases
 * the text "the single most expensive bug a chat can ship", and it is right.
 *
 * DÜZENLE · SİL · YANITLA (sohbet-duzeltme 20 Ekim 2026, denetim H5). Uçlar vardı, ekran
 * bağlamıyordu: yanlış kişiye giden bir mesaj geri alınamıyordu. Kit üç eylemi her mesajın
 * altındaki satırda YAZILI düğmeler olarak çiziyor (üzerine gelince açılmıyor — dokunmatikte ve
 * klavyede de erişiliyor); bu kanca ne yaptıklarını tutuyor:
 *  - «yanıtla» → yazma kutusunun alıntı şeridi; gönderim `replyToMessageId` taşıyor;
 *  - «düzenle» → kendi mesajın, düzenleme penceresinde (`editing`); süre sınırı yok, çünkü sunucuda
 *    yok; kaydedince mesajın altında «düzenlendi»;
 *  - «sil» → onay (`deleting`); sunucuda geri alma yok, mesaj herkes için «Bu mesaj silindi» kalır.
 *  Gönderilemeyen (yerel) bir mesajda «sil» onu bırakıyor, «düzenle» metnini yazma kutusuna geri
 *  koyuyor — sunucunun hiç görmediği bir mesaj için istek gönderilmiyor.
 */

/**
 * A send the server has not confirmed yet. Its `key` is both the local id and the idempotency key.
 *
 * `conversationId` IS PART OF THE ITEM and not read from the closure at send time. One instance of
 * this hook serves every conversation the rail can switch to, and a message that failed in Ayşe's
 * thread and is retried after the reader has clicked Mehmet must go back to Ayşe — under the key
 * the first attempt carried. Reading the current `conversationId` inside `mutationFn` posted it to
 * whichever thread happened to be open, which is a misdelivery, not a glitch.
 */
interface OutboxItem {
  key: string;
  conversationId: string;
  body: string;
  attachmentFileIds: string[];
  files: ChatFile[];
  /** The quote this send answers (H5) — drawn on the pending bubble, sent as `replyToMessageId`. */
  replyTo: ChatReplyRef | null;
  createdAt: string;
}

/** §14b: `POST …/typing` is capped at 120/min — at most one per second, never one per keystroke. */
const TYPING_MIN_INTERVAL_MS = 1000;

/**
 * How far back «go to this message» (a search hit, H6) walks the history before giving up: ten
 * pages of 50. A hit older than that opens the conversation and says so, instead of fetching a
 * year of messages to scroll to one.
 */
const JUMP_MAX_PAGES = 10;

export interface ChatThread {
  /** Hangi konuşmanın kancası — yazma kutusu taslağı bu kimlikle okuyor. */
  conversationId: string | null;
  conversation: ChatConversationDetail | undefined;
  /** Oldest first, outbox appended — exactly what `ChatPanel` renders. */
  messages: ChatMessageItem[];
  members: ChatUser[];
  peer: ChatUser | undefined;
  isLoading: boolean;
  error: string | null;
  retryLoad: () => void;
  hasMoreOlder: boolean;
  isLoadingOlder: boolean;
  loadOlder: () => void;
  firstUnreadId: string | undefined;
  typingUsers: ChatUser[];
  /**
   * Taslaklar DEPODA, bir değer olarak değil: yalnız yazma kutusu abone oluyor ve tuş vuruşu
   * sayfayı yeniden çizmiyor (`chat-draft-store.ts`).
   */
  drafts: DraftStore;
  onDraftChange: (next: string) => void;
  send: () => void;
  isSending: boolean;
  retryMessage: (outboxKey: string) => void;
  download: (file: ChatFile) => void;
  attachments: ReturnType<typeof useChatAttachments>;
  /** Why the composer is closed, or `null` when it is open. */
  blockReason: string | null;
  /** A send this client refused before making the request, phrased for a person. */
  notice: string | null;
  dismissNotice: () => void;
  meId: string;
  /* -------------------------------------------------- H5: reply, edit, delete */
  /** The quote the composer is answering, or `null`. */
  replyTo: ChatReplyRef | null;
  startReply: (messageId: string) => void;
  cancelReply: () => void;
  /** The own message being edited, with the text it had — `null` when the editor is shut. */
  editing: { messageId: string; body: string } | null;
  requestEdit: (messageId: string) => void;
  cancelEdit: () => void;
  saveEdit: (body: string) => void;
  isSavingEdit: boolean;
  /** The message waiting for the delete confirmation, or `null`. */
  deleting: string | null;
  requestDelete: (messageId: string) => void;
  cancelDelete: () => void;
  confirmDelete: () => void;
  isDeleting: boolean;
  /* -------------------------------------------------- H6: go to a message */
  /** A message the surface should scroll to now that it is on screen; `null` otherwise. */
  jumpTargetId: string | null;
  /** The surface scrolled to `jumpTargetId` — the request is spent. */
  jumpDone: () => void;
}

export function useChatThread(options: {
  conversationId: string | null;
  /** The dock passes `false` while it is shut: a closed panel must not read a thread. */
  enabled?: boolean;
  /**
   * A message to open the thread AT (a search hit — H6). Older pages are loaded until it is on
   * screen, up to `JUMP_MAX_PAGES`; `onFocusSettled` is told once the request is spent either way.
   */
  focusMessageId?: string | null;
  onFocusSettled?: () => void;
}): ChatThread {
  const enabled = options.enabled ?? true;
  const conversationId = options.conversationId;
  const queryClient = useQueryClient();
  const { session, permissions } = useAuth();
  const meId = session?.user.id ?? "";
  const restrictedSupport = isRestrictedSupportSession(session);
  const canSend = permissions.can(CHAT_PERMISSIONS.send);

  const conversationQuery = useQuery(chatConversationQuery(conversationId, { enabled }));
  const messagesQuery = useInfiniteQuery(chatMessagesQuery(conversationId, { enabled }));

  const attachments = useChatAttachments({ enabled });

  /**
   * EVERY PIECE OF COMPOSER STATE IS KEYED BY CONVERSATION.
   *
   * This hook is instantiated ONCE per surface — `ChatPage` calls it above the route element, the
   * dock calls it above its own selection — so `/chat/c-1` → `/chat/c-2` does not remount it.
   * Held as one flat `draft`/`outbox`, the text typed to Ayşe was still in the box when Mehmet's
   * thread opened and went to Mehmet on the next Enter, and a failed bubble from Ayşe rendered
   * inside Mehmet's transcript. A draft map (rather than a reset) also means switching back
   * restores what was being written, which is what anyone who has used a chat expects.
   *
   * The drafts live in a STORE, not in state (audit Sohbet 15, measured): state here re-rendered
   * the whole page on every keystroke. Only the composer subscribes to the store. The store is the
   * PERSON's (H10): a new identity is a new store, keyed in this tab's `sessionStorage` by user.
   */
  const drafts = useMemo(() => createDraftStore({ userId: meId }), [meId]);
  const [outbox, setOutbox] = useState<OutboxItem[]>([]);
  const [failedKeys, setFailedKeys] = useState<string[]>([]);
  /** Outbox key → the id the server gave it. Held until the server's own row is on screen. */
  const [confirmedIds, setConfirmedIds] = useState<Record<string, string>>({});
  const [notice, setNotice] = useState<string | null>(null);
  /** The quote each conversation's composer is answering (H5). Keyed like the drafts. */
  const [replyTargets, setReplyTargets] = useState<Record<string, ChatReplyRef>>({});
  const [editing, setEditing] = useState<{ messageId: string; body: string } | null>(null);
  const [deleting, setDeleting] = useState<string | null>(null);

  const clearAttachments = attachments.clear;
  useEffect(() => {
    // The chips belong to the conversation they were attached in: carrying them across would send
    // Ayşe's file to Mehmet, and `clear()` aborts the PUTs that are still running for them.
    clearAttachments();
    setNotice(null);
    // An editor or a confirmation is about a message of the conversation on screen.
    setEditing(null);
    setDeleting(null);
  }, [conversationId, clearAttachments]);

  /**
   * «Yazıyor…» comes from the store the bridge writes into, not from a subscription of its own:
   * one listener per browser, and only the surface that DRAWS the names re-renders
   * (`realtime/typing-store.ts`).
   */
  const typingUserIds = useTypingUserIds(enabled ? conversationId : null);

  const members = useMemo(() => memberIndex(conversationQuery.data), [conversationQuery.data]);

  /** The name on the reader's own bubbles before the server has confirmed them. */
  const me = useMemo<ChatUser>(
    () => members.get(meId) ?? { id: meId, name: "Siz" },
    [members, meId],
  );

  const serverRows = useMemo(() => orderOldestFirst(messagesQuery.data?.pages ?? []), [messagesQuery.data]);
  /** The server's own rows by id — what «düzenle» opens with (the text, not the drawn bubble). */
  const rowById = useMemo(() => new Map<string, ChatMessageRow>(serverRows.map((row) => [row.id, row])), [serverRows]);

  const serverMessages = useMemo(
    () => serverRows.map((row) => messageItem(row, { meId, members })),
    [serverRows, meId, members],
  );

  /**
   * Which message each attachment belongs to.
   *
   * `ChatPanel` hands `onDownload` a `ChatFile` and nothing else, but the download route is
   * addressed by MESSAGE and link: `/chat/messages/{id}/attachments/{linkId}/download`. So the
   * association is recorded while the rows are mapped — the only place both ids are in hand.
   */
  const messageOfLink = useMemo(() => {
    const index = new Map<string, string>();
    for (const page of messagesQuery.data?.pages ?? []) {
      for (const row of page.rows) {
        for (const attachment of row.attachments) index.set(attachment.linkId, row.id);
      }
    }
    return index;
  }, [messagesQuery.data]);

  /**
   * The transcript: the server's thread, plus this conversation's unconfirmed sends.
   *
   * A CONFIRMED ITEM IS DROPPED THE MOMENT THE SERVER'S COPY IS HERE, not a request earlier.
   * `onSuccess` used to remove the outbox entry and only then invalidate, so the user's own
   * message left the screen and came back when the refetch landed — a hole in the transcript for
   * as long as the round trip took. Now the local bubble stays until `serverMessages` actually
   * contains the id the send returned, and is hidden in the same render the server's row appears
   * in, so the two never overlap either.
   */
  const messages = useMemo<ChatMessageItem[]>(() => {
    const onScreen = new Set(serverMessages.map((message) => message.id));
    const pending = outbox
      .filter((item) => item.conversationId === conversationId)
      .filter((item) => {
        const confirmed = confirmedIds[item.key];
        return confirmed === undefined || !onScreen.has(confirmed);
      })
      .map<ChatMessageItem>((item) => ({
        id: item.key,
        author: me,
        sentAt: item.createdAt,
        body: item.body,
        ...(item.files.length > 0 ? { files: item.files } : {}),
        ...(item.replyTo ? { replyTo: item.replyTo } : {}),
        status: failedKeys.includes(item.key)
          ? ("failed" as const)
          : confirmedIds[item.key] !== undefined
            ? ("sent" as const)
            : ("sending" as const),
      }));
    return [...serverMessages, ...pending];
  }, [serverMessages, outbox, failedKeys, confirmedIds, conversationId, me]);

  /** A settled send is swept once its server row is on screen; the memo above already hid it. */
  useEffect(() => {
    const onScreen = new Set(serverMessages.map((message) => message.id));
    const settled = Object.entries(confirmedIds)
      .filter(([, serverId]) => onScreen.has(serverId))
      .map(([key]) => key);
    if (settled.length === 0) return;

    setOutbox((current) => current.filter((item) => !settled.includes(item.key)));
    setConfirmedIds((current) => {
      const next = { ...current };
      for (const key of settled) delete next[key];
      return next;
    });
  }, [serverMessages, confirmedIds]);

  /**
   * The "Yeni mesajlar" rule is drawn from the count the thread OPENED with.
   *
   * Reading `unreadCount` live erased the divider it was meant to draw: opening the thread fires
   * the read-cursor effect below, which invalidates `["chat","conversations"]` — and
   * `keys.chat.conversation(id)` is a child of that prefix, so the detail came back with
   * `unreadCount: 0` one round trip later and the rule vanished. Anchoring it means the divider
   * survives the mark it causes and only moves when the reader leaves and comes back.
   */
  const [unreadAnchor, setUnreadAnchor] = useState<{ id: string | null; count: number } | null>(
    null,
  );
  if (
    conversationQuery.data !== undefined &&
    // Devralınmış bir detay (başka bir sohbetin cevabı) çizgiyi o sohbetin sayısına sabitlerdi.
    !conversationQuery.isPlaceholderData &&
    (unreadAnchor === null || unreadAnchor.id !== conversationId)
  ) {
    setUnreadAnchor({ id: conversationId, count: conversationQuery.data.unreadCount });
  }
  const unreadAtOpen = unreadAnchor?.id === conversationId ? unreadAnchor.count : 0;

  const firstUnreadId = useMemo(
    () => firstUnreadMessageId(serverMessages, unreadAtOpen, meId),
    [serverMessages, unreadAtOpen, meId],
  );

  /* ---------------------------------------------------------------- sending */

  const sendMutation = useMutation({
    mutationFn: (item: OutboxItem) =>
      chatApi.send(
        // The ITEM's conversation, never the one on screen: see `OutboxItem` above.
        item.conversationId,
        {
          body: item.body,
          ...(item.attachmentFileIds.length > 0
            ? { attachmentFileIds: item.attachmentFileIds }
            : {}),
          ...(item.replyTo ? { replyToMessageId: item.replyTo.id } : {}),
        },
        // The SAME key on a retry — that is the entire point of §11. Generating one here would
        // mint a new key on every attempt and store the message twice after a dropped response.
        item.key,
      ),
    onSuccess: async (row, item) => {
      // Close-then-sync, with no hole in between: the id the server gave is remembered, the
      // thread is re-read, and the local bubble is retired only once the server's row is here.
      setConfirmedIds((current) => ({ ...current, [item.key]: row.id }));
      setFailedKeys((current) => current.filter((key) => key !== item.key));
      await invalidateChat(queryClient);
    },
    onError: (error, item) => {
      setFailedKeys((current) => (current.includes(item.key) ? current : [...current, item.key]));
      toast.errorMessage("Mesaj gönderilemedi", chatErrorMessage(error));
    },
  });

  const replyTo = conversationId ? (replyTargets[conversationId] ?? null) : null;

  const send = useCallback(() => {
    if (!conversationId) return;
    const body = drafts.get(conversationId).trim();
    const fileIds = attachments.readyFileIds;

    /**
     * An upload that has not finished is NOT silently dropped.
     *
     * `readyFileIds` is completed uploads only and `clear()` aborts everything else, so sending
     * while a file was still at 40% attached nothing and cancelled the upload without a word —
     * the user's evidence that they attached anything left the screen with the message.
     */
    if (attachments.isUploading) {
      setNotice("Dosya yükleniyor. Bitmesini bekleyip gönderin.");
      return;
    }
    if (body === "" && fileIds.length === 0) return;

    /**
     * THE SERVER'S OWN RULE, CHECKED BEFORE THE REQUEST: a body of at most 8000 characters
     * (§14b). A text-less message with a file is FINE since Sohbet 26 — the server stores it with
     * `body: ""` and previews it as the file's name — and this used to refuse it with «Ek
     * göndermek için kısa bir mesaj yazın», a rule the server had already dropped (found in the
     * 20 Ekim 2026 two-account round). A 400 on a send is not recoverable here: the retry replays
     * the same body under the same idempotency key.
     */
    if (body.length > MESSAGE_MAX_LENGTH) {
      setNotice(`Mesaj en çok ${formatCount(MESSAGE_MAX_LENGTH)} karakter olabilir.`);
      return;
    }

    const files: ChatFile[] = attachments.uploads.filter(isUploadComplete).map((upload) => ({
      id: upload.id,
      name: upload.name,
      // The composer never learns the stored mime type; the bubble only needs to draw a file row
      // until the server's own answer replaces it a moment later.
      mimeType: "application/octet-stream",
      sizeBytes: upload.sizeBytes,
    }));

    const item: OutboxItem = {
      key: localId("snd"),
      conversationId,
      body,
      attachmentFileIds: fileIds,
      files,
      replyTo,
      createdAt: new Date().toISOString(),
    };

    setNotice(null);
    setOutbox((current) => [...current, item]);
    drafts.set(conversationId, "");
    attachments.clear();
    if (replyTo) {
      setReplyTargets((current) => {
        const next = { ...current };
        delete next[conversationId];
        return next;
      });
    }
    sendMutation.mutate(item);
  }, [conversationId, drafts, attachments, sendMutation, replyTo]);

  const retryMessage = useCallback(
    (outboxKey: string) => {
      const item = outbox.find((candidate) => candidate.key === outboxKey);
      if (!item) return;
      setFailedKeys((current) => current.filter((key) => key !== outboxKey));
      sendMutation.mutate(item);
    },
    [outbox, sendMutation],
  );

  /* ---------------------------------------------------- reply · edit · delete (H5) */

  const startReply = useCallback(
    (messageId: string) => {
      if (!conversationId) return;
      // Only a message the server holds can be answered — a local bubble has no id to point at.
      const target = serverMessages.find((message) => message.id === messageId);
      if (!target || target.redactedAt) return;
      setReplyTargets((current) => ({ ...current, [conversationId]: replyRefOf(target) }));
    },
    [conversationId, serverMessages],
  );

  const cancelReply = useCallback(() => {
    if (!conversationId) return;
    setReplyTargets((current) => {
      if (!(conversationId in current)) return current;
      const next = { ...current };
      delete next[conversationId];
      return next;
    });
  }, [conversationId]);

  /** A failed (or still unconfirmed) local bubble: the server never stored it. */
  const outboxItemOf = useCallback(
    (id: string) => outbox.find((item) => item.key === id && item.conversationId === conversationId),
    [outbox, conversationId],
  );

  const discardOutboxItem = useCallback((key: string) => {
    setOutbox((current) => current.filter((item) => item.key !== key));
    setFailedKeys((current) => current.filter((candidate) => candidate !== key));
  }, []);

  const requestEdit = useCallback(
    (messageId: string) => {
      const local = outboxItemOf(messageId);
      if (local) {
        // A message that never reached the server is edited where it was written: back into the
        // composer (only a failed one — a send in flight may still land).
        if (!failedKeys.includes(local.key) || !conversationId) return;
        discardOutboxItem(local.key);
        drafts.set(conversationId, local.body);
        return;
      }
      const row = rowById.get(messageId);
      if (!row || row.redactedAt || row.senderUserId !== meId || row.kind !== "TEXT") return;
      setEditing({ messageId, body: row.body ?? "" });
    },
    [outboxItemOf, failedKeys, conversationId, discardOutboxItem, drafts, rowById, meId],
  );

  const editMutation = useMutation({
    mutationFn: (input: { messageId: string; body: string }) => chatApi.editMessage(input.messageId, input.body),
    onSuccess: async () => {
      setEditing(null);
      await invalidateChat(queryClient);
    },
    onError: (error) => toast.errorMessage("Mesaj düzenlenemedi", chatErrorMessage(error)),
  });

  const saveEdit = useCallback(
    (body: string) => {
      if (!editing) return;
      const trimmed = body.trim();
      // The server would refuse both with a 400; the editor says it instead (see `EditMessageModal`).
      if (trimmed === "" || trimmed.length > MESSAGE_MAX_LENGTH) return;
      if (trimmed === editing.body.trim()) {
        setEditing(null);
        return;
      }
      editMutation.mutate({ messageId: editing.messageId, body: trimmed });
    },
    [editing, editMutation],
  );

  const requestDelete = useCallback(
    (messageId: string) => {
      const local = outboxItemOf(messageId);
      if (local) {
        // A failed local message is let go without a request; one in flight is left to land.
        if (failedKeys.includes(local.key)) discardOutboxItem(local.key);
        return;
      }
      const row = rowById.get(messageId);
      if (!row || row.redactedAt || row.kind !== "TEXT") return;
      setDeleting(messageId);
    },
    [outboxItemOf, failedKeys, discardOutboxItem, rowById],
  );

  const deleteMutation = useMutation({
    mutationFn: (messageId: string) => chatApi.deleteMessage(messageId),
    onSuccess: async (_result, messageId) => {
      setDeleting(null);
      // A quote of the withdrawn message has nothing left to quote.
      setReplyTargets((current) => {
        const entries = Object.entries(current).filter(([, ref]) => ref.id !== messageId);
        return entries.length === Object.keys(current).length ? current : Object.fromEntries(entries);
      });
      await invalidateChat(queryClient);
    },
    onError: (error) => toast.errorMessage("Mesaj silinemedi", chatErrorMessage(error)),
  });

  const confirmDelete = useCallback(() => {
    if (deleting) deleteMutation.mutate(deleting);
  }, [deleting, deleteMutation]);

  /* ---------------------------------------------------------------- typing */

  const lastTypingAt = useRef(0);
  /** Okunuyor, çizilmiyor: tuş vuruşu durum değiştirmemeli (aşağıdaki `setNotice`). */
  const hasNotice = notice !== null;
  const onDraftChange = useCallback(
    (next: string) => {
      if (!conversationId) return;
      drafts.set(conversationId, next);
      // A refusal the user is now editing their way out of must not stay on screen — cleared only
      // when there IS one, so an ordinary keystroke does not touch the page's state at all.
      if (hasNotice) setNotice(null);
      if (next.trim() === "" || !canSend) return;

      const now = Date.now();
      if (now - lastTypingAt.current < TYPING_MIN_INTERVAL_MS) return;
      lastTypingAt.current = now;
      // Fire and forget, and silent on failure: a typing indicator that raises a toast when the
      // rate limit is hit would interrupt the person mid-sentence to report a non-event.
      void chatApi.typing(conversationId).catch(() => undefined);
    },
    [conversationId, canSend, drafts, hasNotice],
  );

  /* ---------------------------------------------------------------- read cursor */

  const markedRef = useRef<string | null>(null);
  useEffect(() => {
    markedRef.current = null;
  }, [conversationId]);

  const threadIsPlaceholder = messagesQuery.isPlaceholderData;
  /**
   * Kişi bakmıyorsa imleç oynamaz: arkadaki sekme ya da başka pencerenin arkasındaki tarayıcı
   * mesajı okumadı (`usePageAttention`). Değer döndüğünde etki yeniden koşuyor — bekleyen
   * okunma o an gidiyor, ayrı bir yeniden deneme yazmaya gerek yok.
   */
  const attentive = usePageAttention();
  useEffect(() => {
    if (!enabled || !conversationId) return;
    if (!attentive) return;
    /**
     * Ekrandaki mesajlar BU sohbetin değilse imleç oynamaz. Devralınmış bir sayfa, önceki
     * sohbetin en yeni mesajını bu sohbetin okundu hedefi yapıyordu.
     */
    if (threadIsPlaceholder) return;
    const target = newestFromOthers(serverMessages, meId);
    // MONOTONIC, and guarded by a ref rather than by state: this effect runs on every thread
    // render, and a state guard would re-enter it once more before settling. The server's cursor
    // only moves forward anyway, so the cost of asking twice is a wasted request, not a wrong
    // one — but 60 wasted requests a minute is its own defect.
    if (!target || markedRef.current === target) return;
    markedRef.current = target;

    void chatApi
      .markRead(conversationId, target)
      .then(async (receipt) => {
        await invalidateChatCounters(queryClient);
        // H4: the read settled the bell's rows about this conversation — the bell follows.
        if (receipt.notificationsCleared > 0) await invalidateBell(queryClient);
      })
      .catch(() => {
        // Let the next message try again: a failed mark is not worth a message to the user, but
        // it must not poison the guard, or the thread stops marking read for the rest of the day.
        markedRef.current = null;
      });
  }, [enabled, conversationId, attentive, threadIsPlaceholder, serverMessages, meId, queryClient]);

  /* ---------------------------------------------------------------- go to a message (H6) */

  const focusMessageId = options.focusMessageId ?? null;
  const onFocusSettled = options.onFocusSettled;
  const [jumpTargetId, setJumpTargetId] = useState<string | null>(null);
  const jumpPages = useRef(0);
  const handledFocus = useRef<string | null>(null);
  useEffect(() => {
    jumpPages.current = 0;
    // A request that was spent and cleared may come back for the SAME message (the same hit
    // clicked twice): it is a new request then.
    if (!focusMessageId) handledFocus.current = null;
  }, [focusMessageId, conversationId]);

  const fetchOlderPage = messagesQuery.fetchNextPage;
  useEffect(() => {
    if (!enabled || !conversationId || !focusMessageId || handledFocus.current === focusMessageId) return;
    if (threadIsPlaceholder || !messagesQuery.data || messagesQuery.isFetching) return;
    if (serverMessages.some((message) => message.id === focusMessageId)) {
      handledFocus.current = focusMessageId;
      setJumpTargetId(focusMessageId);
      return;
    }
    if (messagesQuery.hasNextPage && jumpPages.current < JUMP_MAX_PAGES) {
      jumpPages.current += 1;
      void fetchOlderPage();
      return;
    }
    // Not within reach: the conversation is open, and the person is told where the message is not.
    handledFocus.current = focusMessageId;
    toast.info(
      "Mesaj daha eskide",
      "Sohbet açıldı; aradığınız mesaj yüklenen geçmişin gerisinde. «Daha eski mesajları yükle» ile ulaşabilirsiniz.",
    );
    onFocusSettled?.();
  }, [
    enabled,
    conversationId,
    focusMessageId,
    threadIsPlaceholder,
    messagesQuery.data,
    messagesQuery.isFetching,
    messagesQuery.hasNextPage,
    serverMessages,
    fetchOlderPage,
    onFocusSettled,
  ]);

  const jumpDone = useCallback(() => {
    setJumpTargetId(null);
    onFocusSettled?.();
  }, [onFocusSettled]);

  /* ---------------------------------------------------------------- the rest */

  const download = useCallback(
    (file: ChatFile) => {
      const messageId = messageOfLink.get(file.id);
      if (!messageId) return;
      void chatApi
        .attachmentDownload(messageId, file.id)
        .then((link) => openFileExternally(link.url))
        .catch((error: unknown) => toast.errorMessage("Dosya indirilemedi", chatErrorMessage(error)));
    },
    [messageOfLink],
  );

  const typingUsers = useMemo(
    () => typingUsersFrom(typingUserIds, members),
    [typingUserIds, members],
  );

  const loadOlder = useCallback(() => {
    if (messagesQuery.hasNextPage && !messagesQuery.isFetchingNextPage) {
      void messagesQuery.fetchNextPage();
    }
  }, [messagesQuery]);

  const retryLoad = useCallback(() => {
    void messagesQuery.refetch();
    void conversationQuery.refetch();
  }, [messagesQuery, conversationQuery]);

  const loadError = messagesQuery.error ?? conversationQuery.error;

  const memberList = useMemo(() => [...members.values()], [members]);

  return {
    conversationId,
    conversation: conversationQuery.data,
    messages,
    members: memberList,
    peer: peerOf(conversationQuery.data, meId),
    // A disabled query reports `isPending` forever, so the flag has to say "fetching", not
    // "pending" — a skeleton wired to the latter never resolves in the closed dock.
    isLoading: enabled && conversationId !== null && messagesQuery.isFetching && !messagesQuery.data,
    // `chatErrorMessage`, not `messageFor`: a support session's refusal is a designed one and
    // gets its own sentence ("Destek oturumunda sohbet açılamaz…"), not a generic failure.
    error: loadError ? chatErrorMessage(loadError) : null,
    retryLoad,
    hasMoreOlder: messagesQuery.hasNextPage,
    isLoadingOlder: messagesQuery.isFetchingNextPage,
    loadOlder,
    firstUnreadId,
    typingUsers,
    drafts,
    onDraftChange,
    send,
    isSending: sendMutation.isPending,
    retryMessage,
    download,
    attachments,
    blockReason: composerBlockReason({
      canSend,
      archived: conversationQuery.data?.archived ?? false,
      impersonating: restrictedSupport,
    }),
    notice,
    dismissNotice: () => setNotice(null),
    meId,
    replyTo,
    startReply,
    cancelReply,
    editing,
    requestEdit,
    cancelEdit: () => setEditing(null),
    saveEdit,
    isSavingEdit: editMutation.isPending,
    deleting,
    requestDelete,
    cancelDelete: () => setDeleting(null),
    confirmDelete,
    isDeleting: deleteMutation.isPending,
    jumpTargetId,
    jumpDone,
  };
}
